<script setup lang="ts">
import FsLightbox from "fslightbox-vue/v3";
import { useProductPageStore } from "~/stores/main";
import breakpoints from "#twcss/theme/screens";

// -----------------------
// composables
// -----------------------
const store = useProductPageStore();
const { getAssetUrl, getVariantByProperty, totalStockOfVariant } = useHelpers();
const { t } = useI18n();
const route = useRoute();

// -----------------------
// refs
// -----------------------
const containerWidth = ref<number>(0);
const imageHeight = ref<number>(0);
const headerOffset = ref<number>(0);
const containerTop = ref<number>(0);
const containerHeight = ref<number>(0);
const maximumYPosition = ref<number>(0);
const headerHeight = ref<number>(0);
const lightboxTogglerRef = ref<boolean>(false);
const currentSlideRef = ref<number>(1);
const followScroll = ref(false);
const reachedEnd = ref(false);
const currentImage = ref(0);

// -----------------------
// computed properties
// -----------------------
const images = computed(() => {
  if (!store.masterProduct) {
    return [];
  }

  const products = store.masterProduct.products;
  const productsInStock = products.filter(
    (variant) => totalStockOfVariant(variant) > 0,
  );
  let defaultColor;

  if (products.length === 1) {
    defaultColor = products[0].product.product_szin_property;
  } else if (productsInStock.length > 0) {
    defaultColor = productsInStock[0].product.product_szin_property;
  } else {
    defaultColor = products[0].product.product_szin_property;
  }

  // Select variant with the specified palette on load
  if ("color" in route.query) {
    const color = decodeURIComponent("" + route.query.color);
    const variantWithColor = getVariantByProperty(
      store.masterProduct,
      "product_szin_property",
      color,
    );

    if (variantWithColor) {
      defaultColor = color;
    }
  }

  if (defaultColor !== null) {
    store.color = defaultColor;
    const variantWithColor = getVariantByProperty(
      store.masterProduct,
      "product_szin_property",
      defaultColor,
    );

    if (variantWithColor) {
      return variantWithColor.images.map((src) => getAssetUrl(src));
    }
  }

  return [];
});

const imageMaxHeight = computed(() => {
  // Main & image container top/bottom padding
  const paddings = 4 * 20;

  return `calc(100vh - ${headerOffset.value}px - ${paddings}px)`;
});

// -----------------------
// helper functions
// -----------------------
const openLightBox = (index: number) => {
  if (window.innerWidth < parseInt(breakpoints.lg)) {
    return;
  }

  lightboxTogglerRef.value = !lightboxTogglerRef.value;
  currentSlideRef.value = index;
};

const getContainerElement = () => {
  return document.querySelector(".product-container .column-left");
};

const updateFollowScroll = () => {
  if (
    maximumYPosition.value === 0 ||
    window.innerWidth < parseInt(breakpoints.lg)
  ) {
    return;
  }

  const containerElement = getContainerElement();

  if (!containerElement) {
    return;
  }

  const scrollTop =
    window.scrollY ||
    document.body.scrollTop ||
    document.documentElement.scrollTop;
  const parent = containerElement.parentElement;
  const paddingBottom = parseFloat(getComputedStyle(parent).paddingBottom);

  if (scrollTop >= parent.offsetTop) {
    headerOffset.value = headerHeight.value;
  } else {
    headerOffset.value = parent.offsetTop;
  }

  if (
    scrollTop >
    maximumYPosition.value -
      imageHeight.value -
      headerHeight.value -
      paddingBottom
  ) {
    reachedEnd.value = true;
    return;
  } else {
    reachedEnd.value = false;
  }

  followScroll.value =
    scrollTop + headerHeight.value + paddingBottom >= containerTop.value;
};

const calculateDimensions = () => {
  const containerElement = getContainerElement();

  if (!(containerElement instanceof HTMLElement)) {
    return;
  }

  const parent = containerElement?.parentElement;

  if (!(parent instanceof HTMLElement)) {
    return;
  }

  const parentTop = parent.offsetTop;
  const parentHeight = parent.offsetHeight;
  const paddingTop = parseFloat(getComputedStyle(parent).paddingTop);
  const paddingBottom = parseFloat(getComputedStyle(parent).paddingBottom);
  const imageContainer = parent.querySelector(".image-container");

  if (imageContainer instanceof HTMLElement) {
    imageHeight.value = imageContainer.offsetHeight;
  }

  containerHeight.value = containerElement.offsetHeight;
  containerTop.value = parentTop + paddingTop;
  containerWidth.value = containerElement.offsetWidth;
  maximumYPosition.value = parentTop + parentHeight - paddingBottom;
};

const nextImage = () => {
  if (currentImage.value < images.value.length - 1) {
    currentImage.value++;
  }
};

const previousImage = () => {
  if (currentImage.value > 0) {
    currentImage.value--;
  }
};

const imageLoaded = () => {
  calculateDimensions();
  updateFollowScroll();

  window.addEventListener("resize", () => {
    if (window.innerWidth < parseInt(breakpoints.lg)) {
      reachedEnd.value = false;
      followScroll.value = false;
      return;
    }

    setTimeout(() => {
      calculateDimensions();
      updateFollowScroll();
    }, 100);
  });
};

// -----------------------
// vue events
// -----------------------

if (import.meta.client) {
  onMounted(() => {
    headerHeight.value = document.querySelector("#headerElement").offsetHeight;

    window.addEventListener("scroll", updateFollowScroll);
  });

  watch(images, () => {
    currentImage.value = 0;
  });
}
</script>

<template>
  <!-- Empty div to retain two cols in grid -->
  <div
    v-if="!reachedEnd && followScroll"
    class="column-left col-span-2 lg:col-span-1"
  ></div>
  <div
    class="col-span-2 lg:col-span-1 w-full"
    :class="{
      fixed: !reachedEnd && followScroll,
      'column-left': reachedEnd || !followScroll,
      'place-self-end': reachedEnd,
    }"
    :style="{
      top: `${headerHeight + 20}px`,
      width:
        !reachedEnd && followScroll && containerWidth > 0
          ? `${containerWidth}px`
          : null,
    }"
  >
    <div
      class="image-container grid grid-cols-1 w-full rounded-lg p-5 aspect-square md:aspect-auto"
      :class="{ 'bg-white': images.length > 0 }"
    >
      <!-- Placeholder image -->
      <img
        v-if="images.length === 0"
        src="/images/Image_placeholder.svg"
        class="image-placeholder row-start-1 row-end-1 col-start-1 col-end-2 mx-auto"
        :alt="t('product_page.images_loading')"
      />

      <!-- (Current) Product image -->
      <NuxtImg
        v-if="images.length > 0"
        v-touch:swipe.left="nextImage"
        v-touch:swipe.right="previousImage"
        :src="images[currentImage]"
        format="webp"
        preload
        class="row-start-1 row-end-1 col-start-1 col-end-2 w-auto mx-auto rounded-lg transition-all ease-out duration-300"
        :style="{
          'max-height': imageMaxHeight,
        }"
        :width="700"
        @click="openLightBox(currentImage + 1)"
        @load="imageLoaded"
      ></NuxtImg>

      <!-- Left/right arrows -->
      <button
        v-if="images.length > 0 && currentImage > 0"
        class="flex justify-center items-center row-start-1 row-end-1 col-start-1 col-end-2 justify-self-start place-self-center w-[34px] h-[34px] ml-[-14px] bg-brand-secondary rounded-full shadow-md"
        @click="previousImage"
      >
        <i class="m-icon-arrow-left text-xl ml-[-3px] text-brand-headline"></i>
      </button>
      <button
        v-if="images.length > 0 && currentImage < images.length - 1"
        class="flex justify-center items-center row-start-1 row-end-1 col-start-1 col-end-2 justify-self-end place-self-center w-[34px] h-[34px] mr-[-14px] bg-brand-secondary rounded-full shadow-md"
        @click="nextImage"
      >
        <i class="m-icon-arrow-right text-xl ml-[3px] text-brand-headline"></i>
      </button>

      <!-- Index -->
      <div
        v-if="images.length > 0"
        class="row-start-1 row-end-1 col-start-1 col-end-2 justify-self-end place-self-end mr-[-8px] mb-[-12px] font-bold text-sm"
      >
        {{ currentImage + 1 }}/{{ images.length }}
      </div>
    </div>
  </div>
  <FsLightbox
    v-if="images.length > 0"
    :toggler="lightboxTogglerRef"
    :sources="images"
    :slide="currentSlideRef"
  />
</template>

<style scoped>
@keyframes loading-effect {
  0% {
    opacity: 0.5;
  }

  100% {
    opacity: 1;
  }
}

.image-placeholder {
  animation-name: loading-effect;
  animation-duration: 500ms;
  animation-iteration-count: infinite;
  animation-direction: alternate;
}
</style>
